import { InformationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type { UseFormRegisterReturn, FieldError } from 'react-hook-form';
import Label from './Label';
import { IconEyeOff } from '@/components/ui/icons/IconEyeOff';
import { IconEyeOpen } from '@/components/ui/icons/IconEyeOpen';

type InputWithLabelProps = {
  type: 'text' | 'password';
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  error?: FieldError;
  info?: boolean;
  required?: boolean;
  requiredVariant?: 'asterisk' | 'badge';
  inLine?: boolean;
  disabled?: boolean;
  register: UseFormRegisterReturn;
  color?: string;
  textSize?: 'text-label-16' | 'text-label-14';
};

const InputWithLabel = ({
  placeholder = '',
  type,
  label,
  error,
  required = false,
  requiredVariant = 'asterisk',
  defaultValue,
  disabled = false,
  inLine = false,
  register,
  info = false,
  color,
  textSize = 'text-label-16',
}: InputWithLabelProps) => {
  const normalStyle =
    'text-gray-900 ring-gray-300 placeholder:text-gray-400 focus:ring-indigo-600ext-gray-900 ring-gray-300 placeholder:text-gray-400 focus:ring-gray-300';
  const disabledStyle =
    ' disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200';
  const errorStyle = error
    ? 'text-red-900 ring-red-300 placeholder:text-red-300 focus:!ring-red-300'
    : normalStyle;

  const t = useTranslations();
  const isPassword = type === 'password';
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const inputType = isPassword && isPasswordVisible ? 'text' : type;

  return (
    <div className={clsx({ ['inline-block']: inLine })}>
      {label && (
        <Label
          label={label}
          htmlFor={register.name}
          required={required}
          requiredVariant={requiredVariant}
          color={color}
          textSize={textSize}
        />
      )}
      <div className='relative mt-2 rounded-md'>
        <input
          type={inputType}
          className={clsx(
            disabledStyle,
            errorStyle,
            'block h-[4.6rem] w-full rounded-md border-0 py-1.5 text-[1.6rem] leading-6 ring-1 ring-inset focus:ring-inset',
            isPassword ? 'pl-2x pr-[3.6rem]' : 'px-2x',
          )}
          placeholder={placeholder}
          disabled={disabled}
          defaultValue={defaultValue}
          {...register}
        />
        {isPassword && (
          <button
            type='button'
            onClick={() => setIsPasswordVisible((prev) => !prev)}
            aria-label={t(isPasswordVisible ? 'common-password-hide' : 'common-password-show')}
            className='absolute inset-y-0 right-0 flex items-center pr-3'
          >
            {isPasswordVisible ? (
              <IconEyeOff width='20' height='20' />
            ) : (
              <IconEyeOpen width='20' height='20' />
            )}
          </button>
        )}
        {info && !isPassword && (
          <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3'>
            <InformationCircleIcon className='size-5 text-red-500' aria-hidden='true' />
          </div>
        )}
      </div>
      {error && (
        <p className='mt-2 text-sm text-red-600' id='email-error'>
          {error.message}
        </p>
      )}
    </div>
  );
};

export default InputWithLabel;
